// The cabinet's text: the influencer.web.* translations from the API, kept in this browser between visits and
// brought up to date behind the page (modifiedFrom; the whole namespace every 25th time, which is the only answer that
// can drop a deleted key). A key missing in the chosen language falls back to English, as the Blazor cabinet did;
// one missing in English too renders as nothing.

import { config } from './config.ts';
import { send } from './http.ts';
import { log } from './logs.ts';
import { emit, state } from './state.ts';

const PREFIX = 'influencer.web';
const FULL_SYNC_EVERY = 25;
const CLOCK_SKEW_MARGIN_MS = 5 * 60 * 1000;

type Catalogue = Record<string, string>;

interface CachedCatalogue {
    items: Catalogue;
    syncedAt: number;
    deltas: number;
}

const loaded: Record<string, Catalogue> = {};
const inFlight: Record<string, Promise<void>> = {};

const cacheKey = (locale: string): string => `lify.influencer.translations.${locale}`;

function readCache(locale: string): CachedCatalogue | null {
    try {
        const cached = JSON.parse(localStorage.getItem(cacheKey(locale)) || 'null') as CachedCatalogue | null;

        return cached && cached.items && typeof cached.items === 'object' ? cached : null;
    } catch {
        return null;
    }
}

function writeCache(locale: string, entry: CachedCatalogue): void {
    try {
        localStorage.setItem(cacheKey(locale), JSON.stringify(entry));
    } catch {
        // Quota or storage off: the next visit loads them again.
    }
}

async function fetchTranslations(locale: string, since: number | null): Promise<Catalogue | null> {
    const query = `translations/${encodeURIComponent(locale)}/flat?keyStartsWith=${PREFIX}`
        + (since ? `&modifiedFrom=${encodeURIComponent(new Date(since).toISOString())}` : '');

    const response = await send(`${config.mainApiUrl.replace(/\/$/, '')}/${query}`, { language: locale });
    const items = response.ok ? response.json<Catalogue>() : null;

    if (!items || typeof items !== 'object' || Array.isArray(items)) {
        log.error(`Failed to load translations for locale ${locale}. Status code: ${response.status}. Content: ${response.text.slice(0, 500)}`,
            undefined, undefined, 'Lify.Influencer.Services.Realization.LocalizationService');

        return null;
    }

    return items;
}

async function refresh(locale: string, cached: CachedCatalogue): Promise<void> {
    const startedAt = Date.now();
    const deltas = cached.deltas || 0;
    const since = deltas >= FULL_SYNC_EVERY ? null : cached.syncedAt;
    const items = await fetchTranslations(locale, since);

    if (!items) return;
    if (!since && Object.keys(items).length === 0) return;

    const merged = since ? { ...loaded[locale], ...items } : items;
    const changed = JSON.stringify(merged) !== JSON.stringify(loaded[locale]);

    loaded[locale] = merged;
    writeCache(locale, { items: merged, syncedAt: startedAt - CLOCK_SKEW_MARGIN_MS, deltas: since ? deltas + 1 : 0 });

    if (changed && (locale === state.locale || locale === 'en')) emit('languageChanged');
}

/** Makes a locale's text available: at once from this browser (refreshed behind the page), otherwise once loaded. */
export function ensureTranslations(locale: string | null): Promise<void> {
    if (!locale) return Promise.resolve();
    if (loaded[locale]) return Promise.resolve();

    const pending = inFlight[locale];

    if (pending) return pending;

    const cached = readCache(locale);

    if (cached) {
        loaded[locale] = cached.items;
        refresh(locale, cached).catch((error: unknown) => log.error('Error loading translations', error));

        return Promise.resolve();
    }

    const loading = (async () => {
        const startedAt = Date.now();
        const items = await fetchTranslations(locale, null);

        if (items) {
            loaded[locale] = items;
            writeCache(locale, { items, syncedAt: startedAt - CLOCK_SKEW_MARGIN_MS, deltas: 0 });
        }
    })().finally(() => delete inFlight[locale]);

    inFlight[locale] = loading;

    return loading;
}

export function hasTranslations(locale: string): boolean {
    return !!loaded[locale] || !!readCache(locale);
}

/** The text of a key in the chosen language, or English, or ''. */
export function t(key: string): string {
    const value = (state.locale ? loaded[state.locale]?.[key] : undefined) ?? loaded['en']?.[key];

    return value === undefined || value === null ? '' : value;
}

/** The text of a key, or the given English when the API has none yet. */
export function tf(key: string, fallback: string): string {
    const value = t(key);

    return value && value.trim() ? value : fallback;
}
